import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import { toast } from 'sonner'

import { queryKeys, queryOptions } from '../react-query-config'
import {
  exportAllTenantCharts,
  exportStorageReport,
  exportStorageReportCSV,
  exportTenantChart,
  getLatestSnapshot,
  getStorageReport,
  getTenantAnalytics,
  type StorageReportFilters,
  type TenantAnalyticsFilters,
} from '../storage-reports'
import type {
  LatestSnapshotReportResponseDto,
  StorageReportResponseDto,
  TenantAnalyticsResponseDto,
} from '../types'

// Use centralized query keys from react-query-config

/**
 * Hook to get storage report with global aggregates and tenant data
 */
export const useGetStorageReport = (
  filters?: StorageReportFilters,
  options?: { enabled?: boolean },
): UseQueryResult<StorageReportResponseDto, Error> => {
  const memoizedFilters = useMemo(() => filters || {}, [filters])
  const { enabled = true, ...restOptions } = options || {}

  return useQuery<StorageReportResponseDto>({
    queryKey: queryKeys.reports.list(memoizedFilters),
    queryFn: () => getStorageReport(memoizedFilters),
    enabled,
    ...queryOptions.reports,
    ...restOptions,
  })
}

/**
 * Hook to export storage report as CSV
 */
export const useExportStorageReport = () => {
  return useMutation({
    mutationFn: (filters?: StorageReportFilters) => exportStorageReport(filters),
    onSuccess: blob => {
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = `Storage_Report_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Storage report exported successfully')
    },
    onError: (error: Error) => {
      toast.error('Export failed', {
        description: error.message || 'Failed to export storage report',
      })
    },
  })
}

/**
 * Hook to export comprehensive storage report as CSV
 */
export const useExportStorageReportCSV = () => {
  return useMutation({
    mutationFn: (filters?: StorageReportFilters) => exportStorageReportCSV(filters),
    onSuccess: blob => {
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      // Generate filename with current date in MM-DD-YYYY format
      const today = new Date()
      const dateString = `${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}-${today.getFullYear()}`

      link.href = url
      link.download = `StorageReport_${dateString}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Storage report CSV exported successfully')
    },
    onError: (error: Error) => {
      toast.error('Export failed', {
        description: error.message || 'Failed to export storage report CSV',
      })
    },
  })
}

/**
 * Hook to get detailed analytics for a specific tenant
 */
export const useGetTenantAnalytics = (
  tenantId: string,
  filters?: TenantAnalyticsFilters,
  options?: { enabled?: boolean },
): UseQueryResult<TenantAnalyticsResponseDto, Error> => {
  const memoizedFilters = useMemo(() => filters || {}, [filters])
  const { enabled = true, ...restOptions } = options || {}

  return useQuery<TenantAnalyticsResponseDto>({
    queryKey: queryKeys.reports.tenantAnalyticsWithFilters(tenantId, memoizedFilters),
    queryFn: () => getTenantAnalytics(tenantId, memoizedFilters),
    enabled: enabled && !!tenantId,
    ...queryOptions.reports,
    ...restOptions,
  })
}

/**
 * Hook to export tenant chart data as CSV
 */
export const useExportTenantChart = () => {
  return useMutation({
    mutationFn: ({
      tenantId,
      chartType,
      filters,
    }: {
      tenantId: string
      chartType:
        | 'storage-usage'
        | 'api-usage'
        | 'user-growth'
        | 'bandwidth'
        | 'status-history'
        | 'plan-history'
        | 'all-charts'
      filters?: TenantAnalyticsFilters
    }) => exportTenantChart(tenantId, chartType, filters),
    onSuccess: (blob, { tenantId, chartType, filters }) => {
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      // Format date range for filename
      let dateRange = ''

      if (filters?.startDate && filters.endDate) {
        const startDate = new Date(filters.startDate).toISOString().split('T')[0]
        const endDate = new Date(filters.endDate).toISOString().split('T')[0]

        dateRange = `${startDate}_to_${endDate}`
      } else {
        // Default to last 7 days range
        const endDate = new Date().toISOString().split('T')[0]
        const startDate = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

        dateRange = `${startDate}_to_${endDate}`
      }

      link.href = url
      // Use tenant name if available, otherwise use tenant ID
      const tenantName = (filters as Record<string, string>)?.tenantName || `TenantID${tenantId}`

      link.download = `${tenantName}|${chartType}|${dateRange}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Chart data exported successfully')
    },
    onError: (error: Error) => {
      toast.error('Export failed', {
        description: error.message || 'Failed to export chart data',
      })
    },
  })
}

/**
 * Hook to export all tenant charts as combined CSV
 */
export const useExportAllTenantCharts = () => {
  return useMutation({
    mutationFn: ({ tenantId, filters }: { tenantId: string; filters?: TenantAnalyticsFilters }) =>
      exportAllTenantCharts(tenantId, filters),
    onSuccess: (blob, { tenantId, filters }) => {
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      // Format date range for filename
      let dateRange = ''

      if (filters?.startDate && filters.endDate) {
        const startDate = new Date(filters.startDate).toISOString().split('T')[0]
        const endDate = new Date(filters.endDate).toISOString().split('T')[0]

        dateRange = `${startDate}_to_${endDate}`
      } else {
        // Default to last 7 days range
        const endDate = new Date().toISOString().split('T')[0]
        const startDate = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

        dateRange = `${startDate}_to_${endDate}`
      }

      link.href = url
      // Use tenant name if available, otherwise use tenant ID
      const tenantName = (filters as Record<string, string>)?.tenantName || `TenantID${tenantId}`

      link.download = `${tenantName}|AllCharts|${dateRange}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('All charts exported successfully')
    },
    onError: (error: Error) => {
      toast.error('Export failed', {
        description: error.message || 'Failed to export charts',
      })
    },
  })
}

/**
 * Hook to get latest snapshot date for a tenant
 */
export const useGetLatestSnapshot = (
  tenantId: string,
  options?: { enabled?: boolean },
): UseQueryResult<LatestSnapshotReportResponseDto, Error> => {
  const { enabled = true, ...restOptions } = options || {}

  return useQuery<LatestSnapshotReportResponseDto>({
    queryKey: queryKeys.reports.latestSnapshot(tenantId),
    queryFn: () => getLatestSnapshot(tenantId),
    enabled: enabled && !!tenantId,
    ...queryOptions.reports,
    ...restOptions,
  })
}
